import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { macroDetailData } from '../../../data/macroData';
import { LineChart, Line, XAxis, ResponsiveContainer } from 'recharts';
import { TrendingDown, TrendingUp } from 'lucide-react';

interface FXPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const FXPanel: React.FC<FXPanelProps> = ({ isMinimized, onMinimize }) => {
  const { fx } = macroDetailData;
  const otherFx = fx.reserves - fx.goldReserves;
  const goldPct = Math.round((fx.goldReserves / fx.reserves) * 100);
  const otherPct = 100 - goldPct;

  return (
    <Panel title="FX & RESERVES" subtitle="UZS/USD" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col gap-2 h-full">
        {/* UZS/USD Rate & Regime */}
        <div className="grid grid-cols-2 gap-px bg-terminal-border rounded overflow-hidden">
          <div className="bg-terminal-bg p-2 text-center">
            <div className="text-xs text-gray-500 uppercase">UZS/USD</div>
            <div className="text-lg font-bold text-white">{fx.uzsUsd.toLocaleString()}</div>
            <div className="flex items-center justify-center gap-1 text-data-green text-xs">
              <TrendingUp size={12} />
              <span>+{fx.uzsChange}% YTD</span>
            </div>
          </div>
          <div className="bg-terminal-bg p-2 text-center">
            <div className="text-xs text-gray-500 uppercase">Regime</div>
            <span className="inline-block px-2 py-1 mt-1 bg-data-amber/20 text-data-amber rounded text-xs font-medium">
              {fx.regime}
            </span>
          </div>
        </div>

        {/* 6-Month UZS Trend */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="text-xs text-gray-500 uppercase mb-1">6-Month UZS Trend</div>
          <ResponsiveContainer width="100%" height={50}>
            <LineChart data={fx.history} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
              <XAxis
                dataKey="month"
                tick={{ fontSize: 7, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <Line
                type="monotone"
                dataKey="rate"
                stroke="#ff3b3b"
                strokeWidth={1.5}
                dot={{ fill: '#ff3b3b', r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* FX Reserves & Coverage */}
        <div className="grid grid-cols-2 gap-px bg-terminal-border rounded overflow-hidden">
          <div className="bg-terminal-bg p-2 text-center">
            <div className="text-xs text-gray-500 uppercase">FX Reserves</div>
            <div className="text-lg font-bold text-data-green">${fx.reserves}B</div>
            <div className="flex items-center justify-center gap-1 text-data-green text-xs">
              <TrendingUp size={12} />
              <span>+{Math.round(((fx.reserves - fx.reservesPrev) / fx.reservesPrev) * 100)}% YoY</span>
            </div>
          </div>
          <div className="bg-terminal-bg p-2 text-center">
            <div className="text-xs text-gray-500 uppercase">Coverage</div>
            <div className="text-lg font-bold text-data-green">{fx.reservesMonths}</div>
            <div className="text-xs text-gray-500">months imports</div>
          </div>
        </div>

        {/* Composition */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="text-xs text-gray-500 uppercase mb-1.5">Composition</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-data-amber">Gold Reserves</span>
              <span className="text-white font-mono">${fx.goldReserves}B ({goldPct}%)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Other FX</span>
              <span className="text-white font-mono">${otherFx.toFixed(1)}B ({otherPct}%)</span>
            </div>
          </div>
        </div>

        {/* Intervention Status */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 uppercase">Intervention</span>
            <span className="px-2 py-1 bg-data-green/20 text-data-green rounded text-xs font-medium">
              {fx.interventions}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.CBU} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default FXPanel;
